import React, { useState, useEffect } from 'react';
import { Card, DonutChart, Flex, Metric, Text, Button, LineChart, BarChart } from '@tremor/react';
import { PlayIcon, PauseIcon, ChartBarIcon, VideoCameraIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

// Custom Tooltip Component
function CustomTooltip({ children, tooltip }) {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && tooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 w-80 max-w-sm break-words">
                    {tooltip}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
}

function VideoEmotionAnalysisCard({ fragment, analysisParentUuid, onStartAnalysis, onCancelAnalysis, ...rest }) {
    const { t } = useTranslation();
    const [selectedView, setSelectedView] = useState('overall'); // 'overall', 'timeline', 'frames'
    const [isTimedOut, setIsTimedOut] = useState(false);
    const [tooltipInfo, setTooltipInfo] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [timeInterval, setTimeInterval] = useState(3); // Time interval in seconds for timeline granularity
    const chartContainerRef = React.useRef(null);
    
    // Timeout logic for hanging fragments (longer timeout for video processing)
    useEffect(() => {
        if (!fragment) return;
        
        const pendingStatuses = ['processing', 'pending_webhook', 'queued'];
        if (pendingStatuses.includes(fragment.status)) {
            // Set timeout for 2 hours (7200000ms) for pending video analysis
            const timeoutId = setTimeout(() => {
                setIsTimedOut(true);
            }, 7200000); // 2 hours
            
            return () => clearTimeout(timeoutId);
        } else {
            // Reset timeout if status changes to completed/failed
            setIsTimedOut(false);
        }
    }, [fragment?.status, fragment?.uuid]);
    
    // Handle missing or loading fragment - don't show card by default
    if (!fragment) {
        return null;
    }

    // Handle timeout for pending analysis
    if ((fragment.status === 'processing' || fragment.status === 'pending_webhook' || fragment.status === 'queued') && isTimedOut) {
        return (
            <Card className="max-h-full" data-testid="video-emotion-analysis-card" {...rest}>
                <div className="p-4">
                    <Text className="font-medium text-gray-900">
                        {t('VideoEmotionAnalysis.Title', 'Video Emotion Analysis')}
                    </Text>
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <Text className="text-red-700 font-medium">
                            {t('VideoEmotionAnalysis.Timeout', 'Analysis Timeout')}
                        </Text>
                        <Text className="mt-1 text-sm text-red-600">
                            {t('VideoEmotionAnalysis.TimeoutDescription', 'The video emotion analysis took too long to process. This can happen with longer videos or complex content.')}
                        </Text>
                    </div>
                </div>
            </Card>
        );
    }

    // Handle different fragment statuses (still pending, not timed out yet)
    if (fragment.status === 'processing' || fragment.status === 'pending_webhook' || fragment.status === 'queued') {
        return (
            <Card className="max-h-full" data-testid="video-emotion-analysis-card" {...rest}>
                <div className="p-4">
                    <Text className="font-medium text-gray-900">
                        {t('VideoEmotionAnalysis.Title', 'Video Emotion Analysis')}
                    </Text>
                    <div className="mt-4 flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-iavisus-100"></div>
                        <Text>{getStatusMessage(fragment.status, t)}</Text>
                    </div>
                    {fragment.notes && (
                        <Text className="mt-2 text-sm text-gray-500">{fragment.notes}</Text>
                    )}
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <Text className="text-blue-700 text-sm">
                            {t('VideoEmotionAnalysis.ProcessingNote', 'Video analysis may take several minutes depending on video length and complexity.')}
                        </Text>
                    </div>
                </div>
            </Card>
        );
    }

    // Handle failed analysis - don't show card when analysis fails
    if (fragment.status === 'failed') {
        return null;
    }

    // Handle service unavailable - don't show card when service is unavailable
    if (fragment.status === 'service_unavailable') {
        return null;
    }

    // Handle completed analysis
    if (fragment.status === 'completed' && fragment.result) {
        try {
            const results = typeof fragment.result === 'string' ? JSON.parse(fragment.result) : fragment.result;
            const videoInfo = results.video_info || {};
            const frames = results.frames ? Object.values(results.frames) : [];
            const summary = results.summary || {};
            const processingStats = results.processing_stats || {};
            
            if (!summary.dominant_emotion && frames.length === 0) {
                throw new Error('No analysis results found');
            }

            return (
                <Card className="max-h-full relative" data-testid="video-emotion-analysis-card" {...rest}>
                    {/* Dynamic Tooltip Overlay */}
                    {tooltipInfo && (() => {
                        // Calculate tooltip positioning based on mouse position and available space
                        const tooltipWidth = 280; // Approximate tooltip width
                        const tooltipHeight = 120; // Approximate tooltip height
                        const margin = 16; // Safety margin from screen edges
                        
                        // Get viewport dimensions
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        
                        // Calculate available space on each side
                        const spaceLeft = mousePosition.x - margin;
                        const spaceRight = viewportWidth - mousePosition.x - margin;
                        const spaceTop = mousePosition.y - margin;
                        const spaceBottom = viewportHeight - mousePosition.y - margin;
                        
                        // Determine horizontal position (prefer right, then left)
                        let left, right;
                        if (spaceRight >= tooltipWidth) {
                            // Show to the right of cursor
                            left = mousePosition.x + 12;
                        } else if (spaceLeft >= tooltipWidth) {
                            // Show to the left of cursor
                            right = viewportWidth - mousePosition.x + 12;
                        } else {
                            // Center if neither side has enough space
                            left = Math.max(margin, Math.min(viewportWidth - tooltipWidth - margin, mousePosition.x - tooltipWidth / 2));
                        }
                        
                        // Determine vertical position (prefer above, then below)
                        let top, bottom;
                        if (spaceTop >= tooltipHeight) {
                            // Show above cursor
                            top = mousePosition.y - tooltipHeight - 12;
                        } else if (spaceBottom >= tooltipHeight) {
                            // Show below cursor
                            top = mousePosition.y + 12;
                        } else {
                            // Center vertically if neither has enough space
                            top = Math.max(margin, Math.min(viewportHeight - tooltipHeight - margin, mousePosition.y - tooltipHeight / 2));
                        }
                        
                        // Build the style object
                        const tooltipStyle = {
                            position: 'fixed',
                            zIndex: 9999,
                            ...(left !== undefined && { left: `${left}px` }),
                            ...(right !== undefined && { right: `${right}px` }),
                            ...(top !== undefined && { top: `${top}px` }),
                            ...(bottom !== undefined && { bottom: `${bottom}px` })
                        };
                        
                        return (
                            <div 
                                className="p-3 bg-white text-gray-800 text-sm rounded-lg shadow-lg border border-gray-300 min-w-64 max-w-xs"
                                style={tooltipStyle}
                            >
                                <div className="font-semibold text-center mb-2 text-blue-600">{tooltipInfo.timestamp}</div>
                                <div className="space-y-1">
                                    {tooltipInfo.data && Object.entries(tooltipInfo.data)
                                        .sort(([keyA, valueA], [keyB, valueB]) => {
                                            // Check if these are Action Units (contain "AU")
                                            const isAU_A = keyA.includes('AU');
                                            const isAU_B = keyB.includes('AU');
                                            
                                            if (isAU_A && isAU_B) {
                                                // Both are AUs - sort by AU number
                                                const numA = parseInt(keyA.match(/\d+/)?.[0] || '0');
                                                const numB = parseInt(keyB.match(/\d+/)?.[0] || '0');
                                                return numA - numB;
                                            } else if (!isAU_A && !isAU_B) {
                                                // Both are emotions - sort by value (highest first)
                                                return valueB - valueA;
                                            } else {
                                                // Mixed - shouldn't happen, but fallback to alphabetical
                                                return keyA.localeCompare(keyB);
                                            }
                                        })
                                        .map(([key, value]) => (
                                        <div key={key} className="flex justify-between text-xs">
                                            <span className="text-gray-600">{key}:</span>
                                            <span className="text-gray-800 font-medium">{value}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                    
                    <div className="p-4" ref={chartContainerRef}>
                        <Flex alignItems="center" justifyContent="between" className="mb-4">
                            <div className="flex items-center space-x-2">
                                <Text className="font-medium text-gray-900">
                                    {t('VideoEmotionAnalysis.Title', 'Video Emotion Analysis')}
                                </Text>
                                <CustomTooltip 
                                    tooltip={
                                        <div className="text-left max-w-xs break-words">
                                            <div className="font-semibold text-white mb-2">
                                                {t('VideoEmotionAnalysis.HowItWorks.Title', 'How Emotion Analysis Works')}
                                            </div>
                                            <div className="text-gray-200 text-xs leading-relaxed">
                                                {t('VideoEmotionAnalysis.HowItWorks.Description', 
                                                    'This analysis uses the Facial Action Coding System (FACS), developed by psychologist Paul Ekman. Our AI detects 68 facial landmarks and measures muscle movements (Action Units) to identify the 7 universal emotions. Percentages show confidence levels - they always sum to 100% because each moment represents one emotional state. Higher percentages indicate clearer, more confident emotion detection based on facial expressions.'
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </CustomTooltip>
                            </div>
                            <div className="flex space-x-2">
                                <Button
                                    onClick={() => setSelectedView('overall')}
                                    className={`${selectedView === 'overall' ? '!bg-iavisus-100 !text-white !border-iavisus-100' : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'}`}
                                    size="sm"
                                >
                                    {t('VideoEmotionAnalysis.Overall', 'Overall')}
                                </Button>
                                {frames.length > 0 && (
                                    <Button
                                        icon={ChartBarIcon}
                                        onClick={() => setSelectedView('timeline')}
                                        className={`${selectedView === 'timeline' ? '!bg-iavisus-100 !text-white !border-iavisus-100' : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'}`}
                                        size="sm"
                                    >
                                        {t('VideoEmotionAnalysis.Timeline', 'Timeline')}
                                    </Button>
                                )}
                                {frames.length > 0 && (
                                    <Button
                                        icon={VideoCameraIcon}
                                        onClick={() => setSelectedView('frames')}
                                        className={`${selectedView === 'frames' ? '!bg-iavisus-100 !text-white !border-iavisus-100' : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'}`}
                                        size="sm"
                                    >
                                        {t('VideoEmotionAnalysis.ActionUnitsButton', 'Action Units')}
                                    </Button>
                                )}
                            </div>
                        </Flex>

                        {selectedView === 'overall' && (
                            <>
                                <Flex className="space-x-3 truncate mb-4" justifyContent="start" alignItems="baseline">
                                    <Metric>{getTranslatedEmotionName(summary.dominant_emotion, t)}</Metric>
                                </Flex>

                                {(summary.emotions_detected || frames.length > 0) && (() => {
                                    // Define all standard emotions to ensure complete display
                                    const standardEmotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
                                    
                                    // Calculate emotion counts directly from frame data to ensure consistency with timeline
                                    const emotionCounts = {};
                                    standardEmotions.forEach(emotion => {
                                        emotionCounts[emotion] = 0;
                                    });
                                    
                                    // Count emotions from individual frames (same data source as timeline)
                                    frames.forEach(frame => {
                                        if (frame.emotion_scores) {
                                            // Find the dominant emotion for this frame
                                            let dominantEmotion = null;
                                            let maxScore = 0;
                                            
                                            Object.entries(frame.emotion_scores).forEach(([emotion, score]) => {
                                                if (score > maxScore) {
                                                    maxScore = score;
                                                    dominantEmotion = emotion.toLowerCase();
                                                }
                                            });
                                            
                                            // Map to standard emotion name and increment count
                                            if (dominantEmotion && standardEmotions.includes(dominantEmotion)) {
                                                emotionCounts[dominantEmotion]++;
                                            }
                                        }
                                    });
                                    
                                    // Calculate total frames to convert counts to percentages
                                    const totalFrames = Object.values(emotionCounts).reduce((sum, count) => sum + count, 0);
                                    
                                    // Create emotion data for chart with percentage values
                                    const completeEmotionData = standardEmotions.map(emotion => ({
                                        name: getTranslatedEmotionName(emotion, t),
                                        value: totalFrames > 0 ? Math.round((emotionCounts[emotion] / totalFrames) * 100) : 0,
                                        frameCount: emotionCounts[emotion], // Keep original count for reference
                                        emotion: emotion
                                    }));
                                    
                                    // Show ALL emotions, including those with 0 counts, for complete view
                                    const displayEmotionData = completeEmotionData;
                                    
                                    // If no emotions detected at all, show a message  
                                    if (displayEmotionData.every(item => item.value === 0)) {
                                        return (
                                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                                <Text className="text-yellow-700 font-medium">
                                                    {t('VideoEmotionAnalysis.NoEmotionsDetected', 'No Emotions Detected')}
                                                </Text>
                                                <Text className="mt-1 text-sm text-yellow-600">
                                                    {t('VideoEmotionAnalysis.NoEmotionsDescription', 'No facial emotions were detected in the analyzed video frames.')}
                                                </Text>
                                            </div>
                                        );
                                    }
                                    
                                    return (
                                        <div>
                                            <DonutChart
                                                className="mt-4 h-64"
                                                data={displayEmotionData}
                                                category="value"
                                                index="name"
                                                colors={displayEmotionData.map(item => getEmotionColor(item.emotion))}
                                                showAnimation={true}
                                                variant="pie"
                                                valueFormatter={(value) => `${value}%`}
                                                showLegend={true}
                                                showTooltip={true}
                                            />
                                            
                                            {/* Manual legend as fallback if Tremor legend doesn't show */}
                                            <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                                {displayEmotionData.map((item) => (
                                                    <div key={item.emotion} className="flex items-center space-x-2">
                                                        <div 
                                                            className="w-3 h-3 rounded" 
                                                            style={{
                                                                backgroundColor: getEmotionHexColor(item.emotion)
                                                            }}
                                                        ></div>
                                                        <Text className="text-sm text-gray-700">
                                                            {item.name} ({item.value}%)
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </>
                        )}

                        {selectedView === 'timeline' && frames.length > 0 && (
                            <div>
                                {/* Chart titles ABOVE the chart */}
                                <Text className="mb-2 font-medium text-gray-900">{t('VideoEmotionAnalysis.EmotionTimeline', 'Emotion Timeline')}</Text>
                                <div className="mb-4 flex items-center justify-between">
                                    <Text className="text-xs text-gray-500">
                                        {t('VideoEmotionAnalysis.EmotionTimelineNote', 'Showing emotion scores over time (first 100 frames analyzed)')}
                                    </Text>
                                    
                                    {/* Granularity Selector */}
                                    <div className="flex space-x-1">
                                        <Text className="text-xs text-gray-500 mr-2 self-center">
                                            {t('VideoEmotionAnalysis.Granularity', 'Granularity')}:
                                        </Text>
                                        {[1, 3, 5, 10].map((interval) => (
                                            <Button
                                                key={interval}
                                                onClick={() => setTimeInterval(interval)}
                                                className={`${timeInterval === interval 
                                                    ? '!bg-iavisus-100 !text-white !border-iavisus-100' 
                                                    : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'
                                                } !text-xs !px-2 !py-1`}
                                                size="sm"
                                            >
                                                {interval}s
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                                
                                {(() => {
                                    // Generate chart data once and reuse it
                                    const emotionChartData = (() => {
                                        // Group frames into user-selected intervals and calculate averages
                                        const intervalSize = timeInterval;
                                        const intervals = new Map();
                                        
                                        frames.forEach(frame => {
                                            const timestamp = parseFloat(frame.timestamp || 0);
                                            const intervalKey = Math.floor(timestamp / intervalSize) * intervalSize;
                                            
                                            if (!intervals.has(intervalKey)) {
                                                intervals.set(intervalKey, {
                                                    frames: [],
                                                    timestamp: intervalKey
                                                });
                                            }
                                            
                                            intervals.get(intervalKey).frames.push(frame);
                                        });
                                        
                                        // Calculate averages for each interval and sort by timestamp
                                        return Array.from(intervals.values())
                                            .sort((a, b) => a.timestamp - b.timestamp) // Sort by numeric timestamp first
                                            .map(interval => {
                                                const avgScores = {
                                                    happiness: 0,
                                                    sadness: 0,
                                                    anger: 0,
                                                    fear: 0,
                                                    surprise: 0,
                                                    disgust: 0,
                                                    neutral: 0
                                                };
                                                
                                                // Calculate average emotion scores for this interval
                                                interval.frames.forEach(frame => {
                                                    const emotionScores = frame.emotion_scores || {};
                                                    Object.keys(avgScores).forEach(emotion => {
                                                        avgScores[emotion] += (emotionScores[emotion] || 0);
                                                    });
                                                });
                                                
                                                const frameCount = interval.frames.length;
                                                Object.keys(avgScores).forEach(emotion => {
                                                    avgScores[emotion] = frameCount > 0 ? avgScores[emotion] / frameCount : 0;
                                                });
                                                
                                                return {
                                                    timestamp: formatTimestamp(interval.timestamp),
                                                    [t('VideoEmotionAnalysis.Emotions.Happiness', 'Happiness')]: Math.round(avgScores.happiness * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Sadness', 'Sadness')]: Math.round(avgScores.sadness * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Anger', 'Anger')]: Math.round(avgScores.anger * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Fear', 'Fear')]: Math.round(avgScores.fear * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Surprise', 'Surprise')]: Math.round(avgScores.surprise * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Disgust', 'Disgust')]: Math.round(avgScores.disgust * 100),
                                                    [t('VideoEmotionAnalysis.Emotions.Neutral', 'Neutral')]: Math.round(avgScores.neutral * 100)
                                                };
                                            });
                                    })();
                                    
                                    return (
                                        <div 
                                            className="relative overflow-visible"
                                            onMouseMove={(e) => {
                                                // Calculate mouse position relative to chart
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const x = e.clientX - rect.left;
                                                const y = e.clientY - rect.top;
                                                const width = rect.width;
                                                const height = rect.height;
                                                
                                                // Check if mouse is over the y-axis area (approximately 48px from left)
                                                // Don't show tooltip if hovering over y-axis
                                                if (x < 48) {
                                                    setTooltipInfo(null);
                                                    return;
                                                }
                                                
                                                // Store absolute mouse position for tooltip positioning
                                                setMousePosition({ x: e.clientX, y: e.clientY });
                                                
                                                // Calculate which data point we're hovering over
                                                const dataIndex = Math.min(
                                                    Math.max(0, Math.floor(((x - 48) / (width - 48)) * emotionChartData.length)),
                                                    emotionChartData.length - 1
                                                );
                                                
                                                if (emotionChartData[dataIndex]) {
                                                    const dataPoint = emotionChartData[dataIndex];
                                                    setTooltipInfo({
                                                        timestamp: dataPoint.timestamp,
                                                        data: Object.fromEntries(
                                                            Object.entries(dataPoint).filter(([key, value]) => 
                                                                key !== 'timestamp' && 
                                                                typeof value === 'number' && 
                                                                !isNaN(value) // Include all emotions, even 0% values
                                                            )
                                                        )
                                                    });
                                                }
                                            }}
                                            onMouseLeave={() => setTooltipInfo(null)}
                                        >
                                            <LineChart
                                                className="h-64"
                                                data={emotionChartData}
                                                index="timestamp"
                                    categories={[
                                        t('VideoEmotionAnalysis.Emotions.Happiness', 'Happiness'),
                                        t('VideoEmotionAnalysis.Emotions.Sadness', 'Sadness'),
                                        t('VideoEmotionAnalysis.Emotions.Anger', 'Anger'),
                                        t('VideoEmotionAnalysis.Emotions.Fear', 'Fear'),
                                        t('VideoEmotionAnalysis.Emotions.Surprise', 'Surprise'),
                                        t('VideoEmotionAnalysis.Emotions.Disgust', 'Disgust'),
                                        t('VideoEmotionAnalysis.Emotions.Neutral', 'Neutral')
                                    ]}
                                    colors={["emerald", "blue", "red", "violet", "orange", "lime", "gray"]}
                                    valueFormatter={(value) => `${value}%`}
                                    yAxisWidth={48}
                                    showLegend={false}
                                    showGridLines={true}
                                    showTooltip={false}
                                    connectNulls={true}
                                    curveType="monotone"
                                />
                                
                                {/* Custom legend below chart */}
                                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                    {[
                                        { name: t('VideoEmotionAnalysis.Emotions.Happiness', 'Happiness'), color: '#10b981' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Sadness', 'Sadness'), color: '#3b82f6' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Anger', 'Anger'), color: '#ef4444' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Fear', 'Fear'), color: '#8b5cf6' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Surprise', 'Surprise'), color: '#f97316' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Disgust', 'Disgust'), color: '#65a30d' },
                                        { name: t('VideoEmotionAnalysis.Emotions.Neutral', 'Neutral'), color: '#6b7280' }
                                    ].map((emotion) => (
                                        <div key={emotion.name} className="flex items-center space-x-2">
                                            <div 
                                                className="w-3 h-3 rounded" 
                                                style={{ backgroundColor: emotion.color }}
                                            ></div>
                                            <Text className="text-sm text-gray-700">{emotion.name}</Text>
                                        </div>
                                    ))}
                                </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {selectedView === 'frames' && frames.length > 0 && (
                            <div>
                                {/* Chart titles ABOVE the chart */}
                                <Text className="mb-2 font-medium text-gray-900">{t('VideoEmotionAnalysis.ActionUnitsTimeline', 'Action Units Intensity Timeline')}</Text>
                                <div className="mb-4 flex items-center justify-between">
                                    <Text className="text-xs text-gray-500">
                                        {t('VideoEmotionAnalysis.ActionUnitsTimelineNote', 'Showing facial action unit intensity levels over time (first 100 frames analyzed)')}
                                    </Text>
                                    
                                    {/* Granularity Selector */}
                                    <div className="flex space-x-1">
                                        <Text className="text-xs text-gray-500 mr-2 self-center">
                                            {t('VideoEmotionAnalysis.Granularity', 'Granularity')}:
                                        </Text>
                                        {[1, 3, 5, 10].map((interval) => (
                                            <Button
                                                key={interval}
                                                onClick={() => setTimeInterval(interval)}
                                                className={`${timeInterval === interval 
                                                    ? '!bg-iavisus-100 !text-white !border-iavisus-100' 
                                                    : '!bg-gray-100 !text-gray-700 hover:!bg-iavisus-100 hover:!text-white !border-iavisus-100 hover:!border-iavisus-100'
                                                } !text-xs !px-2 !py-1`}
                                                size="sm"
                                            >
                                                {interval}s
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                                
                                {(() => {
                                    
                                    // Extract Action Units data with user-selected intervals
                                    const auData = (() => {
                                        // Group frames into user-selected intervals and calculate averages
                                        const intervalSize = timeInterval;
                                        const intervals = new Map();
                                        
                                        frames.forEach(frame => {
                                            const timestamp = parseFloat(frame.timestamp || 0);
                                            const intervalKey = Math.floor(timestamp / intervalSize) * intervalSize;
                                            
                                            if (!intervals.has(intervalKey)) {
                                                intervals.set(intervalKey, {
                                                    frames: [],
                                                    timestamp: intervalKey
                                                });
                                            }
                                            
                                            intervals.get(intervalKey).frames.push(frame);
                                        });
                                        
                                        // Calculate averages for each interval and sort by timestamp
                                        return Array.from(intervals.values())
                                            .sort((a, b) => a.timestamp - b.timestamp) // Sort by numeric timestamp first
                                            .map(interval => {
                                                const data = {
                                                    timestamp: formatTimestamp(interval.timestamp),
                                                };
                                                
                                                // Get all unique AU keys from frames in this interval
                                                const auKeys = new Set();
                                                interval.frames.forEach(frame => {
                                                    const actionUnits = frame.action_units || {};
                                                    Object.keys(actionUnits).forEach(au => {
                                                        auKeys.add(au);
                                                    });
                                                });
                                                
                                                // Calculate average for each AU in this interval
                                                auKeys.forEach(au => {
                                                    let sum = 0;
                                                    let count = 0;
                                                    
                                                    interval.frames.forEach(frame => {
                                                        const actionUnits = frame.action_units || {};
                                                        const value = actionUnits[au];
                                                        
                                                        if (value !== undefined && value !== null) {
                                                            let finalValue = 0;
                                                            
                                                            if (typeof value === 'number') {
                                                                finalValue = value <= 1 ? value * 100 : value;
                                                            } else if (typeof value === 'string') {
                                                                const numericValue = parseFloat(value);
                                                                if (!isNaN(numericValue)) {
                                                                    finalValue = numericValue <= 1 ? numericValue * 100 : numericValue;
                                                                } else if (value === 'true' || value === '1') {
                                                                    finalValue = 100;
                                                                } else if (value === 'false' || value === '0') {
                                                                    finalValue = 0;
                                                                }
                                                            } else if (typeof value === 'boolean') {
                                                                finalValue = value ? 100 : 0;
                                                            } else if (typeof value === 'object' && value !== null) {
                                                                if (value.intensity !== undefined) {
                                                                    const intensity = parseFloat(value.intensity);
                                                                    if (!isNaN(intensity)) {
                                                                        finalValue = Math.abs(intensity) <= 1 ? Math.abs(intensity) * 100 : Math.abs(intensity);
                                                                    } else if (value.activated === true) {
                                                                        finalValue = 100;
                                                                    }
                                                                } else if (value.activated !== undefined) {
                                                                    finalValue = value.activated ? 100 : 0;
                                                                } else if (Array.isArray(value) && value.length > 0) {
                                                                    const numVal = parseFloat(value[0]) || 0;
                                                                    finalValue = numVal <= 1 ? numVal * 100 : numVal;
                                                                }
                                                            }
                                                            
                                                            sum += finalValue;
                                                            count++;
                                                        }
                                                    });
                                                    
                                                    // Store average value for this AU in this interval
                                                    if (count > 0) {
                                                        data[translateActionUnit(au, t)] = Math.round(sum / count);
                                                    }
                                                });
                                                
                                                return data;
                                            });
                                    })();
                                    
                                    // Determine which AUs are present from all available data
                                    const presentAUs = new Set();
                                    frames.forEach(frame => {
                                        const actionUnits = frame.action_units || {};
                                        Object.keys(actionUnits).forEach(au => {
                                            // Show all AUs that exist, regardless of value
                                            if (actionUnits[au] !== undefined && actionUnits[au] !== null) {
                                                presentAUs.add(au);
                                            }
                                        });
                                    });
                                    
                                    const categories = Array.from(presentAUs).slice(0, 15).map(au => translateActionUnit(au, t)); // Show up to 15 AUs
                                    
                                    // Show message if no categories found
                                    if (categories.length === 0) {
                                        return (
                                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                                                <Text className="text-yellow-700 font-medium">{t('VideoEmotionAnalysis.NoActionUnits', 'No Action Units Available')}</Text>
                                                <Text className="mt-1 text-sm text-yellow-600">
                                                    {t('VideoEmotionAnalysis.NoActionUnitsDescription', 'Action Units field exists but contains no valid data for visualization.')}
                                                </Text>
                                            </div>
                                        );
                                    }
                                    
                                    return (
                                        <>
                                            <div 
                                                className="relative overflow-visible"
                                                onMouseMove={(e) => {
                                                    // Calculate mouse position relative to chart
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const x = e.clientX - rect.left;
                                                    const y = e.clientY - rect.top;
                                                    const width = rect.width;
                                                    const height = rect.height;
                                                    
                                                    // Check if mouse is over the y-axis area (approximately 48px from left)
                                                    // Don't show tooltip if hovering over y-axis
                                                    if (x < 48) {
                                                        setTooltipInfo(null);
                                                        return;
                                                    }
                                                    
                                                    // Store absolute mouse position for tooltip positioning
                                                    setMousePosition({ x: e.clientX, y: e.clientY });
                                                    
                                                    // Calculate which data point we're hovering over
                                                    const dataIndex = Math.min(
                                                        Math.max(0, Math.floor(((x - 48) / (width - 48)) * auData.length)),
                                                        auData.length - 1
                                                    );
                                                    
                                                    if (auData[dataIndex]) {
                                                        const dataPoint = auData[dataIndex];
                                                        setTooltipInfo({
                                                            timestamp: dataPoint.timestamp,
                                                            data: Object.fromEntries(
                                                                Object.entries(dataPoint).filter(([key, value]) => 
                                                                    key !== 'timestamp' && 
                                                                    typeof value === 'number' && 
                                                                    !isNaN(value) && // Include all AU values, even 0%
                                                                    key.includes('AU') // Ensure it's actually an AU
                                                                )
                                                            )
                                                        });
                                                    }
                                                }}
                                                onMouseLeave={() => setTooltipInfo(null)}
                                            >
                                                <LineChart
                                                className="h-64"
                                                data={auData}
                                                index="timestamp"
                                                categories={categories}
                                                colors={["blue", "red", "emerald", "violet", "amber", "cyan", "pink", "lime", "gray", "fuchsia", "indigo", "rose", "orange", "teal", "purple"]}
                                                valueFormatter={(value) => `${value}%`}
                                                yAxisWidth={48}
                                                showLegend={false}
                                                showGridLines={true}
                                                showTooltip={false}
                                                connectNulls={true}
                                                curveType="monotone"
                                                />
                                            </div>
                                            
                                            {/* Custom legend below Action Units chart */}
                                            <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                                {categories.slice(0, 15).filter(category => {
                                                    // Only show AUs that have valid codes and friendly names
                                                    const auCode = getActionUnitCode(category);
                                                    const friendlyName = getActionUnitFriendlyName(category);
                                                    return auCode && friendlyName && auCode.match(/AU\d+/);
                                                }).map((category, index) => {
                                                    const colors = ["#3b82f6", "#ef4444", "#10b981", "#8b5cf6", "#f59e0b", "#06b6d4", "#ec4899", "#84cc16", "#6b7280", "#d946ef", "#6366f1", "#f43f5e", "#f97316", "#14b8a6", "#a855f7"];
                                                    // Extract AU code and friendly name
                                                    const auCode = getActionUnitCode(category);
                                                    const friendlyName = getActionUnitFriendlyName(category);
                                                    
                                                    
                                                    return (
                                                        <CustomTooltip 
                                                            key={category} 
                                                            tooltip={friendlyName || auCode}
                                                        >
                                                            <div className="flex items-center space-x-2 cursor-help hover:bg-gray-50 px-1 py-1 rounded transition-colors">
                                                                <div 
                                                                    className="w-3 h-3 rounded" 
                                                                    style={{ backgroundColor: colors[index] || '#6b7280' }}
                                                                ></div>
                                                                <span className="text-sm text-gray-700">
                                                                    {auCode}
                                                                </span>
                                                            </div>
                                                        </CustomTooltip>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                        

                        {fragment.cost && fragment.cost > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                                <Text className="text-xs text-gray-500">
                                    {t('VideoEmotionAnalysis.Cost', 'Analysis Cost')}: ${fragment.cost}
                                </Text>
                            </div>
                        )}
                    </div>
                </Card>
            );
        } catch (error) {
            console.error('Error rendering video emotion analysis results:', error);
            return (
                <Card className="max-h-full" data-testid="video-emotion-analysis-card" {...rest}>
                    <div className="p-4">
                        <Text className="font-medium text-gray-900">
                            {t('VideoEmotionAnalysis.Title', 'Video Emotion Analysis')}
                        </Text>
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <Text className="text-yellow-700">
                                {t('VideoEmotionAnalysis.DataError', 'Error displaying analysis results')}
                            </Text>
                        </div>
                    </div>
                </Card>
            );
        }
    }

    // Default fallback
    return (
        <Card className="max-h-full" data-testid="video-emotion-analysis-card" {...rest}>
            <div className="p-4">
                <Text className="font-medium text-gray-900">
                    {t('VideoEmotionAnalysis.Title', 'Video Emotion Analysis')}
                </Text>
                <Text className="mt-2 text-gray-500">
                    {t('VideoEmotionAnalysis.NoData', 'No analysis data available')}
                </Text>
            </div>
        </Card>
    );
}

// Helper functions
function getStatusMessage(status, t) {
    switch (status) {
        case 'queued':
            return t('VideoEmotionAnalysis.Status.Queued', 'Video queued for analysis...');
        case 'processing':
            return t('VideoEmotionAnalysis.Status.Processing', 'Processing video emotions...');
        case 'pending_webhook':
            return t('VideoEmotionAnalysis.Status.PendingResults', 'Analyzing video emotions...');
        default:
            return t('VideoEmotionAnalysis.Status.Unknown', 'Processing...');
    }
}

function translateEmotionName(emotion, t) {
    if (!emotion) return t('EmotionAnalysis.Unknown', 'Unknown');
    
    const emotionKey = `EmotionAnalysis.${emotion.charAt(0).toUpperCase() + emotion.slice(1)}`;
    const translationMap = {
        'EmotionAnalysis.Happiness': t('EmotionAnalysis.Happiness', 'Happiness'),
        'EmotionAnalysis.Sadness': t('EmotionAnalysis.Sadness', 'Sadness'),
        'EmotionAnalysis.Anger': t('EmotionAnalysis.Anger', 'Anger'),
        'EmotionAnalysis.Fear': t('EmotionAnalysis.Fear', 'Fear'),
        'EmotionAnalysis.Surprise': t('EmotionAnalysis.Surprise', 'Surprise'),
        'EmotionAnalysis.Disgust': t('EmotionAnalysis.Disgust', 'Disgust'),
        'EmotionAnalysis.Neutral': t('EmotionAnalysis.Neutral', 'Neutral'),
    };
    
    return translationMap[emotionKey] || emotion.charAt(0).toUpperCase() + emotion.slice(1);
}

function getTranslatedEmotionName(emotion, t) {
    if (!emotion) return t('VideoEmotionAnalysis.Emotions.Unknown', 'Unknown');
    
    const emotionKey = emotion.toLowerCase();
    switch (emotionKey) {
        case 'happiness':
            return t('VideoEmotionAnalysis.Emotions.Happiness', 'Happiness');
        case 'sadness':
            return t('VideoEmotionAnalysis.Emotions.Sadness', 'Sadness');
        case 'anger':
            return t('VideoEmotionAnalysis.Emotions.Anger', 'Anger');
        case 'fear':
            return t('VideoEmotionAnalysis.Emotions.Fear', 'Fear');
        case 'surprise':
            return t('VideoEmotionAnalysis.Emotions.Surprise', 'Surprise');
        case 'disgust':
            return t('VideoEmotionAnalysis.Emotions.Disgust', 'Disgust');
        case 'neutral':
            return t('VideoEmotionAnalysis.Emotions.Neutral', 'Neutral');
        default:
            return emotion.charAt(0).toUpperCase() + emotion.slice(1);
    }
}

function translateActionUnit(au, t) {
    if (!au) return au;
    
    const auKey = au.toUpperCase();
    
    // Helper function to get the friendly name
    const getFriendlyName = (code) => {
        switch (code) {
            case 'AU1':
            case 'AU01':
                return t('VideoEmotionAnalysis.ActionUnits.AU01', 'Inner Brow Raiser');
            case 'AU2':
            case 'AU02':
                return t('VideoEmotionAnalysis.ActionUnits.AU02', 'Outer Brow Raiser');
            case 'AU3':
            case 'AU03':
                return t('VideoEmotionAnalysis.ActionUnits.AU03', 'Brow Lowerer (Inner)');
            case 'AU4':
            case 'AU04':
                return t('VideoEmotionAnalysis.ActionUnits.AU04', 'Brow Lowerer');
            case 'AU5':
            case 'AU05':
                return t('VideoEmotionAnalysis.ActionUnits.AU05', 'Upper Lid Raiser');
            case 'AU6':
            case 'AU06':
                return t('VideoEmotionAnalysis.ActionUnits.AU06', 'Cheek Raiser');
            case 'AU7':
            case 'AU07':
                return t('VideoEmotionAnalysis.ActionUnits.AU07', 'Lid Tightener');
            case 'AU8':
            case 'AU08':
                return t('VideoEmotionAnalysis.ActionUnits.AU08', 'Lips Toward Each Other');
            case 'AU9':
            case 'AU09':
                return t('VideoEmotionAnalysis.ActionUnits.AU09', 'Nose Wrinkler');
            case 'AU10':
                return t('VideoEmotionAnalysis.ActionUnits.AU10', 'Upper Lip Raiser');
            case 'AU11':
                return t('VideoEmotionAnalysis.ActionUnits.AU11', 'Nasolabial Deepener');
            case 'AU12':
                return t('VideoEmotionAnalysis.ActionUnits.AU12', 'Lip Corner Puller');
            case 'AU14':
                return t('VideoEmotionAnalysis.ActionUnits.AU14', 'Dimpler');
            case 'AU15':
                return t('VideoEmotionAnalysis.ActionUnits.AU15', 'Lip Corner Depressor');
            case 'AU17':
                return t('VideoEmotionAnalysis.ActionUnits.AU17', 'Chin Raiser');
            case 'AU20':
                return t('VideoEmotionAnalysis.ActionUnits.AU20', 'Lip Stretcher');
            case 'AU23':
                return t('VideoEmotionAnalysis.ActionUnits.AU23', 'Lip Tightener');
            case 'AU25':
                return t('VideoEmotionAnalysis.ActionUnits.AU25', 'Lips Part');
            case 'AU26':
                return t('VideoEmotionAnalysis.ActionUnits.AU26', 'Jaw Drop');
            case 'AU45':
                return t('VideoEmotionAnalysis.ActionUnits.AU45', 'Blink');
            default:
                return null; // No translation found
        }
    };
    
    const friendlyName = getFriendlyName(auKey);
    
    // Return format: "AU1 - Inner Brow Raiser" or just the original code if no translation
    if (friendlyName) {
        return `${auKey} - ${friendlyName}`;
    } else {
        return au; // Return original if not found in mapping
    }
}

function getEmotionColor(emotion) {
    const colorMap = {
        'happiness': 'emerald',  // emerald - positive, growth, joy
        'sadness': 'blue',       // blue - melancholy, calm sadness
        'anger': 'red',          // red - intensity, aggression
        'fear': 'violet',        // violet - uncertainty, anxiety
        'surprise': 'orange',    // orange - attention-grabbing, sudden
        'disgust': 'lime',       // lime - unpleasant, earthy
        'neutral': 'gray',       // gray - balanced, no emotion
    };
    return colorMap[emotion?.toLowerCase()] || 'gray';
}

function getEmotionHexColor(emotion) {
    const colorMap = {
        'happiness': '#10b981', // emerald-500 - positive, growth, joy
        'sadness': '#3b82f6',   // blue-500 - melancholy, calm sadness
        'anger': '#ef4444',     // red-500 - intensity, aggression
        'fear': '#8b5cf6',      // violet-500 - uncertainty, anxiety
        'surprise': '#f97316',  // orange-500 - attention-grabbing, sudden
        'disgust': '#65a30d',   // lime-600 - unpleasant, earthy
        'neutral': '#6b7280',   // gray-500 - balanced, no emotion
    };
    return colorMap[emotion?.toLowerCase()] || '#6b7280';
}

function formatDuration(seconds) {
    if (!seconds) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
}

function formatTimestamp(seconds) {
    if (!seconds) return '0:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Helper functions to extract AU code and friendly name for legend display
function getActionUnitCode(category) {
    // Extract just the AU code part from "AU1 - Inner Brow Raiser" format
    if (typeof category === 'string' && category.includes(' - ')) {
        return category.split(' - ')[0];
    }
    // If it's already just the AU code, return as is
    return category;
}

function getActionUnitFriendlyName(category) {
    // Extract the friendly name directly from "AU1 - Inner Brow Raiser" format
    if (typeof category === 'string' && category.includes(' - ')) {
        const parts = category.split(' - ');
        if (parts.length >= 2) {
            return parts.slice(1).join(' - '); // Join back in case there are multiple dashes
        }
    }
    // If no friendly name found, return null
    return null;
}

export default VideoEmotionAnalysisCard;